/**
 * The route's ONE composer, sending on lane v2.
 *
 * `MultiAgentRoute` owns the textarea, the attachments (`useAttachments`), the mic and Send/Stop; on a
 * v2 conversation those same controls submit through `laneV2SessionStore.sendTurn` (`POST
 * /api/sessions/turns`) instead of `POST /api/runs`. This hook is the v2 half of that decision: what
 * "in flight" means on a session, what the placeholder says, what Send refuses, and when the
 * attachments that rode a turn may be cleared. It reads the session store and nothing else — the
 * run stores are v1's, and a v2 turn never touches them.
 */
import { useCallback, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import type { MultiAgentAttachment } from "@/services/multi-agent/api";
import { laneV2IdeState } from "@/services/multi-agent/ideState";
import { sendableAttachments, toAttachmentIn } from "@/services/multi-agent/laneV2/composerAttachments";
import { composerPlaceholder, planDecisionPending, turnInFlight, withoutForwardDeployAsks } from "@/services/multi-agent/laneV2/conversation";
import { canSendFollowUp, type FollowUp } from "@/services/multi-agent/laneV2/followUps";
import { pendingAsks } from "@/services/multi-agent/laneV2/reduce";
import { clearAccepted, sendWithInputs } from "@/services/multi-agent/laneV2/turnSend";
import { useComposerResetStore } from "@/stores/composerResetStore";
import { useLaneV2SessionStore } from "@/stores/laneV2SessionStore";
import { useMultiAgentIdeStore } from "@/pages/multi-agent/ide/store/multiAgentIdeStore";

export interface UseLaneV2ComposerArgs {
  attachments: readonly MultiAgentAttachment[];
  /** Remove ONE composer attachment by id (a send removes exactly the ones it carried). */
  removeAttachment: (id: string) => void;
}

export interface LaneV2Composer {
  /** A turn is running server-side, or a send is still in flight: Send becomes Stop (unless there is text to add, see `canFollowUp`). */
  inFlight: boolean;
  /**
   * A turn is running AND a message can be added to it: the page knows the turn's id (its `turn_opened` frame arrived) and
   * fewer than the limit are still waiting on it. `send` then goes to the turn's inbox instead of being refused.
   */
  canFollowUp: boolean;
  /** What was sent while a turn ran, and where each stands; for the strip in the composer dock. */
  followUps: readonly FollowUp[];
  /** Send one of those again as a normal message, and drop its row. */
  resendFollowUp: (followUp: FollowUp) => Promise<void>;
  dismissFollowUp: (followUp: FollowUp) => void;
  /** R0 `offline` — Send refuses; typing, declining and cancelling stay allowed. */
  offline: boolean;
  /** A transient notice from the store ("busy, retrying"), for the composer dock. */
  notice: string | null;
  /** A refresh after a live update failed: the view may be out of date. */
  stale: boolean;
  /** Reload the conversation from the server (clears `stale` when it succeeds). */
  refresh: () => Promise<void>;
  /** A question is waiting: the composer is also where a typed reply answers it. */
  askPending: boolean;
  placeholder: string;
  /**
   * Submit. Resolves once the request is in flight (the outcome arrives by stream); false when nothing was sent (an
   * attachment cap refused it, or the store could not start the turn) so the caller keeps the text.
   */
  send: (text: string) => Promise<boolean>;
  /** Start a turn with this text only: no paperclips, no context chip, never the running turn's inbox ("Try again"). */
  sendTextOnly: (text: string) => Promise<boolean>;
  stop: () => Promise<void>;
}

/** The in-context file chip, if one is set. */
function pendingContextFile() {
  return useMultiAgentIdeStore.getState().pendingRefactorFile;
}

export function useLaneV2Composer({ attachments, removeAttachment }: UseLaneV2ComposerArgs): LaneV2Composer {
  const state = useLaneV2SessionStore(
    useShallow((s) => ({
      activeTurnId: s.activeTurnId,
      localTurns: s.localTurns,
      asks: s.asks,
      snapshots: s.snapshots,
      draft: s.draft,
      tasks: s.tasks,
      turns: s.turns,
      offline: s.offline,
      notice: s.notice,
      turnAttachments: s.turnAttachments,
      turnIdeState: s.turnIdeState,
      followUps: s.followUps,
      stale: s.stale,
    })),
  );

  /*
   * A turn sent WITH attachments keeps them on screen until the server has ACCEPTED that turn — `sendTurn` resolves once
   * the request is merely in flight, so clearing on that promise alone raced ahead of a 413/503 the request could still
   * draw; the failure copy ("remove some and try again") needs something on screen to point at. A refused or failed send
   * leaves them for the reader to retry or remove.
   *
   * The clear is the SEND'S OWN: `onAccepted` closes over the attachment ids and chip that this send carried, so a second
   * send before the first answers, or a "New session" in between, can neither lose it nor make it clear someone else's
   * files. (It used to be one shared "pending" slot that the next send overwrote and a store reset emptied.)
   */
  const [refusal, setRefusal] = useState<string | null>(null);

  const send = useCallback(
    async (text: string): Promise<boolean> => {
      setRefusal(null);
      const carried = sendableAttachments(attachments);
      const carriedIds = carried.map((a) => a.id);
      const epochAtSend = useComposerResetStore.getState().epoch;
      const outcome = await sendWithInputs({
        store: useLaneV2SessionStore.getState(),
        text,
        chip: pendingContextFile(),
        paperclips: carried.map(toAttachmentIn),
        repoIdeState: laneV2IdeState(),
        onAccepted: (rode) =>
          clearAccepted(rode, carriedIds, epochAtSend, {
            epoch: () => useComposerResetStore.getState().epoch,
            removeAttachment,
            currentChip: pendingContextFile,
            clearChip: () => useMultiAgentIdeStore.getState().setPendingRefactorFile(null),
          }),
        // The composer's thumbnail of each image this send carries, so the sent question can show the picture: MSA keeps
        // only the text it read from an image, never the image.
        previews: Object.fromEntries(
          carried.flatMap((a) => (a.kind === "image" && a.thumbnailDataUrl ? [[a.name, a.thumbnailDataUrl]] : [])),
        ),
      });
      if (outcome.kind === "refused") {
        setRefusal(outcome.refusal);
        return false;
      }
      return outcome.kind === "follow_up" || outcome.started;
    },
    [attachments, removeAttachment],
  );

  const sendTextOnly = useCallback(async (text: string): Promise<boolean> => {
    const store = useLaneV2SessionStore.getState();
    if (store.activeTurnId) return false;
    const outcome = await sendWithInputs({ store, text, chip: null, paperclips: [], repoIdeState: laneV2IdeState() });
    return outcome.kind === "turn" && outcome.started;
  }, []);

  const refresh = useCallback(() => useLaneV2SessionStore.getState().reconnect(), []);
  const stop = useCallback(() => useLaneV2SessionStore.getState().cancelActiveTurn(), []);
  const dismissFollowUp = useCallback((f: FollowUp) => useLaneV2SessionStore.getState().dismissFollowUp(f.localId), []);
  const resendFollowUp = useCallback(
    async (f: FollowUp) => {
      useLaneV2SessionStore.getState().dismissFollowUp(f.localId);
      await send(f.text);
    },
    [send],
  );

  const inFlight = turnInFlight(state);
  const canFollowUp = inFlight && canSendFollowUp(state.followUps, state.activeTurnId);
  const askPending = pendingAsks([...withoutForwardDeployAsks(state.asks, state.snapshots)]).length > 0;
  return {
    inFlight,
    offline: state.offline,
    notice: refusal ?? state.notice,
    stale: state.stale,
    refresh,
    askPending,
    canFollowUp,
    followUps: state.followUps,
    resendFollowUp,
    dismissFollowUp,
    placeholder: inFlight
      ? canFollowUp
        ? "Weave is working. Add to this or correct it…"
        : state.activeTurnId
          ? "Wait for the reply before sending more…"
          : "Weave is starting…"
      : state.offline
        ? "Sending is turned off for maintenance right now…"
        : askPending && !planDecisionPending(state.asks)
          ? "Pick an option above, or type a reply…"
          : composerPlaceholder(state),
    send,
    sendTextOnly,
    stop,
  };
}
